/**
 * ModeSelector - 撮影モード選択コンポーネント
 */

import { useState } from 'react'

export type CaptureMode = 'normal' | 'panorama'

interface ModeSelectorProps {
  onModeSelect: (mode: CaptureMode) => void
  onCancel: () => void
}

export function ModeSelector({ onModeSelect, onCancel }: ModeSelectorProps) {
  const [selectedMode, setSelectedMode] = useState<CaptureMode | null>(null)

  const handleModeClick = (mode: CaptureMode) => {
    setSelectedMode(mode)
    setTimeout(() => onModeSelect(mode), 200) // アニメーション後に遷移
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      {/* ヘッダー */}
      <div className="flex items-center justify-between p-4 bg-gray-800">
        <button
          onClick={onCancel}
          className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </button>
        <h2 className="text-lg font-semibold">撮影モード選択</h2>
        <div className="w-10"></div>
      </div>

      {/* メインコンテンツ */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pb-20">
        <h1 className="text-2xl font-bold mb-2">撮影モードを選択</h1>
        <p className="text-gray-400 mb-8 text-center">
          撮影する内容に応じて最適なモードを選んでください
        </p>

        {/* 通常撮影モード */}
        <button
          onClick={() => handleModeClick('normal')}
          className={`w-full max-w-md mb-4 p-6 rounded-xl transition-all transform hover:scale-105 ${
            selectedMode === 'normal' 
              ? 'bg-blue-600 ring-2 ring-blue-400' 
              : 'bg-gray-800 hover:bg-gray-700'
          }`}
        >
          <div className="flex items-start">
            <div className="text-4xl mr-4">📷</div>
            <div className="flex-1 text-left">
              <h3 className="text-lg font-semibold mb-1">通常撮影</h3>
              <div className="text-sm text-gray-300 space-y-1">
                <p>移動しながら撮影</p>
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <span className="px-2 py-0.5 bg-gray-700 rounded">縦向き推奨</span>
                  <span className="px-2 py-0.5 bg-gray-700 rounded">直線移動向け</span>
                </div>
              </div>
            </div>
          </div>
        </button>

        {/* パノラマ撮影モード */}
        <button
          onClick={() => handleModeClick('panorama')}
          className={`w-full max-w-md mb-6 p-6 rounded-xl transition-all transform hover:scale-105 ${
            selectedMode === 'panorama' 
              ? 'bg-purple-600 ring-2 ring-purple-400' 
              : 'bg-gray-800 hover:bg-gray-700'
          }`}
        >
          <div className="flex items-start">
            <div className="text-4xl mr-4">🌐</div>
            <div className="flex-1 text-left">
              <h3 className="text-lg font-semibold mb-1">パノラマ撮影</h3>
              <div className="text-sm text-gray-300 space-y-1">
                <p>360°風景を撮影</p>
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <span className="px-2 py-0.5 bg-gray-700 rounded">横向き推奨</span>
                  <span className="px-2 py-0.5 bg-gray-700 rounded">広角レンズ対応</span>
                </div>
              </div>
            </div>
          </div>
        </button>

        {/* ヘルプテキスト */}
        <div className="text-center">
          <button className="text-sm text-blue-400 hover:text-blue-300 transition-colors">
            <span className="inline-flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              モードについて詳しく
            </span>
          </button>
        </div>
      </div>
    </div>
  )
}